// 中文注释：集中化权限管理系统
// 统一管理所有权限配置，避免分散定义导致的不一致问题

// 角色常量定义
export const ROLE_OPERATOR = "operator"; // 操作员
export const ROLE_ADMIN = "admin"; // 管理员
export const ROLE_SUPER_ADMIN = "super_admin"; // 超级管理员

// 权限点常量定义
export const PERMISSION_BASIC_VIEW = "basic.view"; // 基础查看权限
export const PERMISSION_DEVICE_MANAGE = "device.manage"; // 设备管理
export const PERMISSION_DEVICE_EDIT = "device.edit"; // 设备编辑权限
export const PERMISSION_DEVICE_DELETE = "device.delete"; // 设备删除权限
export const PERMISSION_DEVICE_CREATE = "device.create"; // 设备创建权限
export const PERMISSION_DEVICE_CALL = "device.call"; // 设备呼叫权限
export const PERMISSION_CALL_VIEW = "call.view"; // 通话记录查看
export const PERMISSION_RECORDING_VIEW = "recording.view"; // 录音查看权限
export const PERMISSION_RECORDING_DELETE = "recording.delete"; // 录音删除权限
export const PERMISSION_RECORDING_MANAGE = "recording.manage"; // 录音管理（包含所有录音操作）
export const PERMISSION_OTA_MANAGE = "ota.manage"; // OTA升级管理
export const PERMISSION_USER_MANAGE = "user.manage"; // 用户管理
export const PERMISSION_SIMCARD_MANAGE = "simcard.manage"; // SIM卡管理
export const PERMISSION_RTC_STATS = "rtc.stats"; // RTC统计
export const PERMISSION_PACKAGE_MANAGE = "package.manage"; // 套餐管理
export const PERMISSION_TENANT_MANAGE = "tenant.manage";
export const PERMISSION_TENANT_APIKEY_MANAGE = "tenant.apikey.manage";

// 角色权限映射
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  [ROLE_OPERATOR]: [
    PERMISSION_BASIC_VIEW,
    PERMISSION_DEVICE_MANAGE, // 操作员可以查看设备管理
    PERMISSION_DEVICE_CALL, // 操作员可以呼叫设备
    PERMISSION_CALL_VIEW,
    PERMISSION_RECORDING_VIEW, // 操作员可以查看录音，但不能删除
    // 操作员拥有基础功能、设备查看、呼叫、通话记录查看、录音查看权限，但不能编辑/删除/创建设备和录音
  ],
  [ROLE_ADMIN]: [
    PERMISSION_BASIC_VIEW,
    PERMISSION_DEVICE_MANAGE,
    PERMISSION_DEVICE_EDIT,
    PERMISSION_DEVICE_DELETE,
    PERMISSION_DEVICE_CREATE,
    // PERMISSION_DEVICE_CALL, // 管理员不能呼叫设备，只能查看
    PERMISSION_CALL_VIEW,
    PERMISSION_RECORDING_VIEW, // 录音查看权限
    PERMISSION_RECORDING_DELETE, // 录音删除权限
    PERMISSION_OTA_MANAGE,
    PERMISSION_USER_MANAGE,
    PERMISSION_TENANT_APIKEY_MANAGE,
    // 管理员拥有设备、通话、录音查看/删除、OTA、用户管理等权限，但不能呼叫设备
  ],
  [ROLE_SUPER_ADMIN]: [
    PERMISSION_BASIC_VIEW,
    PERMISSION_DEVICE_MANAGE,
    PERMISSION_DEVICE_EDIT,
    PERMISSION_DEVICE_DELETE,
    PERMISSION_DEVICE_CREATE,
    // PERMISSION_DEVICE_CALL, // 超级管理员不能呼叫设备，只能查看
    PERMISSION_CALL_VIEW,
    PERMISSION_RECORDING_VIEW, // 录音查看权限
    PERMISSION_RECORDING_DELETE, // 录音删除权限
    PERMISSION_OTA_MANAGE,
    PERMISSION_USER_MANAGE,
    PERMISSION_SIMCARD_MANAGE,
    PERMISSION_RTC_STATS,
    PERMISSION_PACKAGE_MANAGE, // 套餐管理权限
    PERMISSION_TENANT_MANAGE,
    PERMISSION_TENANT_APIKEY_MANAGE,
    // 超级管理员拥有所有管理权限，但不能呼叫设备
  ],
};

// 路由权限配置 - 集中管理所有路由的权限
export const ROUTE_PERMISSIONS: Record<
  string,
  {
    path: string;
    name: string;
    roles: string[];
    permission?: string; // 对应的权限点（可选）
  }
> = {
  dashboard: {
    path: "/dashboard",
    name: "首页",
    roles: [ROLE_OPERATOR, ROLE_ADMIN, ROLE_SUPER_ADMIN],
    permission: PERMISSION_BASIC_VIEW,
  },
  deviceManagement: {
    path: "/deviceManagement",
    name: "设备管理",
    roles: [ROLE_OPERATOR, ROLE_ADMIN, ROLE_SUPER_ADMIN],
    permission: PERMISSION_DEVICE_MANAGE,
  },
  callRecords: {
    path: "/callRecords",
    name: "通话记录",
    roles: [ROLE_OPERATOR, ROLE_ADMIN, ROLE_SUPER_ADMIN],
    permission: PERMISSION_CALL_VIEW,
  },
  ota: {
    path: "/ota",
    name: "OTA升级",
    roles: [ROLE_ADMIN, ROLE_SUPER_ADMIN],
    permission: PERMISSION_OTA_MANAGE,
  },
  recordingManagement: {
    path: "/recordingManagement",
    name: "录音管理",
    roles: [ROLE_OPERATOR, ROLE_ADMIN, ROLE_SUPER_ADMIN],
    permission: PERMISSION_RECORDING_VIEW, // 改为查看权限，所有角色都可以访问页面
  },
  simCardManagement: {
    path: "/simCardList",
    name: "SIM卡管理",
    roles: [ROLE_SUPER_ADMIN],
    permission: PERMISSION_SIMCARD_MANAGE,
  },
  rtcStats: {
    path: "/rtcStats",
    name: "RTC统计",
    roles: [ROLE_SUPER_ADMIN],
    permission: PERMISSION_RTC_STATS,
  },
  packageManagement: {
    path: "/packageManagement",
    name: "套餐管理",
    roles: [ROLE_SUPER_ADMIN],
    permission: PERMISSION_PACKAGE_MANAGE,
  },
  tenantManagement: {
    path: "/tenantManagement",
    name: "租户管理",
    roles: [ROLE_SUPER_ADMIN],
    permission: PERMISSION_TENANT_MANAGE,
  },
  tenantApiKeys: {
    path: "/tenantApiKeys",
    name: "API密钥",
    roles: [ROLE_ADMIN],
    permission: PERMISSION_TENANT_APIKEY_MANAGE,
  },
};

// 导航菜单配置 - 基于路由权限配置生成
export const NAVIGATION_MENU_ITEMS = Object.values(ROUTE_PERMISSIONS).map(
  (route) => ({
    path: route.path,
    name: route.name,
    roles: route.roles,
  })
);

// 路由守卫权限映射 - 基于路由权限配置生成
export const ROUTE_GUARD_PERMISSIONS = Object.fromEntries(
  Object.values(ROUTE_PERMISSIONS).map((route) => [route.path, route.roles])
);

// 权限检查工具函数
export const hasRoutePermission = (
  userRole: string,
  routePath: string
): boolean => {
  const routeConfig = Object.values(ROUTE_PERMISSIONS).find(
    (route) => route.path === routePath
  );
  return routeConfig ? routeConfig.roles.includes(userRole) : false;
};

export const hasPermission = (
  userRole: string,
  permission: string
): boolean => {
  const permissions = ROLE_PERMISSIONS[userRole] || [];
  return permissions.includes(permission);
};

export const getAvailableRoutes = (userRole: string) => {
  return Object.values(ROUTE_PERMISSIONS).filter((route) =>
    route.roles.includes(userRole)
  );
};

// TypeScript 类型定义
export type UserRole =
  | typeof ROLE_OPERATOR
  | typeof ROLE_ADMIN
  | typeof ROLE_SUPER_ADMIN;

export type Permission =
  | typeof PERMISSION_BASIC_VIEW
  | typeof PERMISSION_DEVICE_MANAGE
  | typeof PERMISSION_DEVICE_EDIT
  | typeof PERMISSION_DEVICE_DELETE
  | typeof PERMISSION_DEVICE_CREATE
  | typeof PERMISSION_DEVICE_CALL
  | typeof PERMISSION_CALL_VIEW
  | typeof PERMISSION_RECORDING_VIEW
  | typeof PERMISSION_RECORDING_DELETE
  | typeof PERMISSION_RECORDING_MANAGE
  | typeof PERMISSION_OTA_MANAGE
  | typeof PERMISSION_USER_MANAGE
  | typeof PERMISSION_SIMCARD_MANAGE
  | typeof PERMISSION_RTC_STATS
  | typeof PERMISSION_PACKAGE_MANAGE
  | typeof PERMISSION_TENANT_MANAGE
  | typeof PERMISSION_TENANT_APIKEY_MANAGE;

export interface RoutePermission {
  path: string;
  name: string;
  roles: UserRole[];
  permission?: Permission;
}
